const SESSION_KEY = 'autojobs_drive_token'

export interface DriveFileItem {
  id: string
  name: string
  mimeType: string
  size: string
  modifiedTime: string
}

export function getAutoJobsDriveToken(): string | null {
  return sessionStorage.getItem(SESSION_KEY)
}

export function saveAutoJobsDriveToken(token: string): void {
  sessionStorage.setItem(SESSION_KEY, token)
}

export function clearAutoJobsDriveToken(): void {
  sessionStorage.removeItem(SESSION_KEY)
}

export function initiateAutoJobsOAuth(): void {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
  if (!clientId) throw new Error('VITE_GOOGLE_CLIENT_ID is not configured')

  const redirectUri = `${window.location.origin}/jobs/autojobs`
  const scope = 'https://www.googleapis.com/auth/drive.readonly'
  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth')

  authUrl.searchParams.set('response_type', 'token')
  authUrl.searchParams.set('client_id', clientId)
  authUrl.searchParams.set('redirect_uri', redirectUri)
  authUrl.searchParams.set('scope', scope)

  window.location.href = authUrl.toString()
}

export function parseTokenFromHash(): string | null {
  const hash = window.location.hash
  if (!hash.includes('access_token')) return null

  const params = new URLSearchParams(hash.substring(1))
  const token = params.get('access_token')

  if (token) {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search)
  }

  return token
}

export async function listFolderFiles(folderId: string, accessToken: string): Promise<DriveFileItem[]> {
  const query = `'${folderId}' in parents and trashed=false`
  const allFiles: DriveFileItem[] = []
  let pageToken: string | undefined

  do {
    const url = new URL('https://www.googleapis.com/drive/v3/files')

    url.searchParams.set('q', query)
    url.searchParams.set('fields', 'files(id,name,mimeType,size,modifiedTime),nextPageToken')
    url.searchParams.set('pageSize', '1000')
    url.searchParams.set('supportsAllDrives', 'true')
    url.searchParams.set('includeItemsFromAllDrives', 'true')

    if (pageToken) {
      url.searchParams.set('pageToken', pageToken)
    }

    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${accessToken}` }
    })

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('UNAUTHORIZED')
      }
      if (res.status === 403) {
        throw new Error('PERMISSION_DENIED')
      }
      throw new Error(`Drive API error: ${res.status}`)
    }

    const data = await res.json()
    allFiles.push(...(data.files || []))
    pageToken = data.nextPageToken
  } while (pageToken)

  return allFiles.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
}

export async function downloadFileAsBase64(fileId: string, accessToken: string): Promise<string> {
  const url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&supportsAllDrives=true`

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` }
  })

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('UNAUTHORIZED')
    }
    throw new Error(`Failed to download file: ${res.statusText}`)
  }

  const blob = await res.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function extractFolderIdFromUrl(url: string): string | null {
  // Format 1: https://drive.google.com/drive/folders/{ID}
  const match1 = url.match(/\/folders\/([a-zA-Z0-9_-]{20,})/)
  if (match1) return match1[1]

  // Format 2: https://drive.google.com/open?id={ID}&usp=drive_fs
  const match2 = url.match(/[?&]id=([a-zA-Z0-9_-]{20,})/)
  if (match2) return match2[1]

  return null
}
